import React, {FC} from 'react';

import {Dropdown} from '@optimaxdev/design-system';
import {addDays, format} from 'date-fns';
import {connect} from 'react-redux';

import {CloudinaryImage} from 'components/cloudinaryImage/cloudinaryImage';
import {Price} from 'components/price/price';
import {VistaPlusInfoRow} from 'desktop/pages/cart/components/vistaPlusInfoRow/vistaPlusInfoRow';
import {updateCartItem} from 'desktop/pages/cart/store/reducers/changeItemQty/changeItemQty';
import {getIsUpdateItemPending} from 'desktop/pages/cart/store/selectors';
import {AddToCartType} from 'desktop/pages/cart/types/quoteItem';
import {ApplicationStoreType} from 'desktop/store';
import {
    SubscriptionDropdownPlanType,
    useSubscriptionPlansRequest,
} from 'mobile/features/cLSubscription/hooks/useSubscriptionPlans/useSubscriptionPlans';

import recharge from '../../cLSubscription/img/recharge.svg?url';

import s from './vistaPlusSubscription.scss';

const dropdownStyles = {container: s.dropdown};

const LENSES_PER_BOX = 30;

export type PropsType = {
    quoteItemId: string;
    productId: string;
    currentSubscriptionPlan?: string;
    updateItem: (data: {data: AddToCartType; quoteType?: string}) => void;
    isUpdateItemPending: boolean;
    price?: string;
};

/**
 * Adds 10 days to the current date
 * @returns {string} - next shipment date
 */
const getNextShipmentDate = () => format(addDays(Date.now(), 10), 'MMMM d');

/**
 * Returns the plan price
 * @param {string} price - price
 * @param {SubscriptionDropdownPlanType} currentPlan - subscription plan
 * @returns {React.ReactElement} - price for the subscription plan
 */
const getPlanPrice = ({countOfMonths, label}: SubscriptionDropdownPlanType, price?: string) => (
    <>
        <Price>{Number(price) * countOfMonths * 2}</Price>
        &nbsp;every&nbsp;{label.toLowerCase()}
    </>
);

/**
 * Returns the number of boxes for the subscription plan
 * @param {number} boxes - number of boxes per eye
 * @returns {number} - number of boxes per plan
 */
const getBoxesPerPlan = (boxes: number) => boxes * LENSES_PER_BOX * 2;

/**
 * Returns subscription plans formatted for dropdown
 * @param {SubscriptionDropdownPlanType[]} plans - original plan types
 * @returns {SubscriptionDropdownPlanType[]} - formatted plan types
 */
export const getPlansForDropdown = (plans: SubscriptionDropdownPlanType[]) =>
    plans.map(({label, ...plan}) => ({
        ...plan,
        label: `Supply every ${label.toLowerCase()}`,
    }));

/**
 * Vista plus subscription plan selection component
 * @param {PropsType} - props
 * @returns {React.ReactElement} - rendered component
 */
export const VistaPlusSubscription: FC<PropsType> = ({
    currentSubscriptionPlan,
    isUpdateItemPending,
    price,
    productId,
    quoteItemId,
    updateItem,
}) => {
    const {
        data: plans = [],
        isError,
        isInitialLoading: isPlansLoading,
    } = useSubscriptionPlansRequest(productId);

    const selectPlan = (value: React.ReactNode) => {
        updateItem({data: {currentSubscriptionPlan: String(value), quoteItemId}});
    };
    const isDisabled = isUpdateItemPending || isPlansLoading;
    const currentPlan = plans.find(({value}) => value === currentSubscriptionPlan);

    if (isError) return null;

    return (
        <aside className={s.container}>
            <CloudinaryImage className={s.recharge} src={recharge} />
            <span className={s.title}>Auto Refill</span>
            <span className={s.subtitle}>
                After your 10-day trial, we will automatically refill your supply every month. You
                can cancel anytime.
            </span>
            {currentPlan && (
                <VistaPlusInfoRow
                    className={s.row24}
                    info={getPlanPrice(currentPlan, price)}
                    title="Price:"
                />
            )}
            <VistaPlusInfoRow
                className={s.row24}
                info={getNextShipmentDate()}
                title="Next shipment:"
            />
            <VistaPlusInfoRow
                info={`${getBoxesPerPlan(
                    Number(currentPlan?.boxesPerEye),
                )} lenses, for ${currentPlan?.label.toLowerCase()}`}
                title="Quantity:"
            />
            <Dropdown
                classes={dropdownStyles}
                disabled={isDisabled}
                onClick={selectPlan}
                options={getPlansForDropdown(plans)}
                selectedValue={currentSubscriptionPlan}
                size="small"
            />
        </aside>
    );
};

const mapStateToProps = (store: ApplicationStoreType) => ({
    isUpdateItemPending: getIsUpdateItemPending(store),
});

const mapDispatchToProps = {
    updateItem: updateCartItem,
};

export const VistaPlusSubscriptionConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(VistaPlusSubscription);
